import { getSessionDataFromUrl } from './urlDecoder';

const STORAGE_KEYS = {
  SESSION_DATA: 'aether_session_data',
  DEVICE_ID: 'aether_device_id',
  ORG_ID: 'aether_org_id',
  ACCESS_KEY: 'aether_access_key',
  VALIDATION_SHOWN: 'aether_validation_shown',
};

const calculateTokenExpiry = (expiresIn) => {
  if (!expiresIn) return null;
  const expiresInNum = parseInt(expiresIn);
  if (isNaN(expiresInNum)) return null;
  const currentTime = Math.floor(Date.now() / 1000);
  // If value is already a Unix timestamp (> 1 year in seconds), use as-is
  return expiresInNum > 31536000 ? expiresInNum : currentTime + expiresInNum;
};

export const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
  }
  return deviceId;
};

export const initializeSession = (url = window.location.href) => {
  const existingSession = getStoredSession();
  if (existingSession) {
    return existingSession;
  }

  const sessionData = getSessionDataFromUrl(url);
  if (!sessionData) {
    return null;
  }

  const deviceId = sessionData.deviceId || getOrCreateDeviceId();

  const expiresIn = sessionData.expiresIn || sessionData.expires_in || null;
  const tokenExpiry = sessionData.tokenExpiry
    ? parseInt(sessionData.tokenExpiry)
    : calculateTokenExpiry(expiresIn);

  const session = {
    orgId: sessionData.orgId || sessionData.org_id || null,
    accessKey: sessionData.accessKey || sessionData.access_key || null,
    refreshToken: sessionData.refreshToken || sessionData.refresh_token || null,
    sessionStartTime: sessionData.sessionStartTime || Date.now(),
    deviceId: deviceId,
    userId: sessionData.userId || sessionData.user_id || null,
    username: sessionData.username || sessionData.user_name || null,
    email: sessionData.email || null,
    role: sessionData.role || null,
    os: sessionData.os || null,
    browser: sessionData.browser || null,
    screenResolution: sessionData.screenResolution || null,
    platform: sessionData.platform || null,
    ...sessionData,
    expiresIn: expiresIn,
    tokenExpiry: tokenExpiry,
  };

  if (session.refresh_token && !session.refreshToken) {
    session.refreshToken = session.refresh_token;
  }

  if (!session.orgId) {
    return null;
  }

  try {
    saveSessionToStorage(session);

    const verify = getStoredSession();
    if (!verify || !verify.orgId) {
      try {
        localStorage.setItem(STORAGE_KEYS.SESSION_DATA, JSON.stringify(session));
        const verifyAgain = getStoredSession();
        if (!verifyAgain || !verifyAgain.orgId) {
          return null;
        }
      } catch (retryError) {
        return null;
      }
    }
  } catch (error) {
    return null;
  }

  return session;
};

export const saveSessionToStorage = (session) => {
  try {
    const sessionJson = JSON.stringify(session);
    localStorage.setItem(STORAGE_KEYS.SESSION_DATA, sessionJson);

    const verify = localStorage.getItem(STORAGE_KEYS.SESSION_DATA);
    if (!verify) {
      throw new Error('Session data was not saved');
    }

    if (session.orgId) {
      localStorage.setItem(STORAGE_KEYS.ORG_ID, session.orgId);
    }
    if (session.accessKey) {
      localStorage.setItem(STORAGE_KEYS.ACCESS_KEY, session.accessKey);
    }
    if (session.deviceId) {
      localStorage.setItem(STORAGE_KEYS.DEVICE_ID, session.deviceId);
    }
    if (session.refreshToken || session.refresh_token) {
      const refreshToken = session.refreshToken || session.refresh_token;
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('aether_refresh_token', refreshToken);
    }
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.error('LocalStorage quota exceeded');
    }
    throw error;
  }
};

export const getStoredSession = () => {
  try {
    const sessionData = localStorage.getItem(STORAGE_KEYS.SESSION_DATA);
    if (!sessionData) {
      return null;
    }
    return JSON.parse(sessionData);
  } catch (error) {
    console.error('Error reading session from storage:', error);
    return null;
  }
};

export const getCurrentSession = (url = window.location.href) => {
  const stored = getStoredSession();
  if (stored) {
    return stored;
  }
  return initializeSession(url);
};

export const shouldShowValidation = (session) => {
  if (!session || !session.orgId) {
    return false;
  }

  const validationShown = localStorage.getItem(STORAGE_KEYS.VALIDATION_SHOWN);
  const lastSessionId = localStorage.getItem(`${STORAGE_KEYS.VALIDATION_SHOWN}_session`);
  const currentSessionId = `${session.orgId}_${session.deviceId}`;

  if (!validationShown || lastSessionId !== currentSessionId) {
    return true;
  }

  return false;
};

export const markValidationShown = (session) => {
  if (!session || !session.orgId) {
    return;
  }

  const currentSessionId = `${session.orgId}_${session.deviceId}`;
  localStorage.setItem(STORAGE_KEYS.VALIDATION_SHOWN, 'true');
  localStorage.setItem(`${STORAGE_KEYS.VALIDATION_SHOWN}_session`, currentSessionId);
};

// OPUS-901/902: exchange a secure ?h= handoff (server-minted, short-TTL) for a
// real session. The refresh token arrives in the response body, never the URL.
// Returns the stored session on success, else null (falls back to legacy ?session=).
export const exchangeHandoffFromUrl = async () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const handoff = params.get('h');
    if (!handoff) return null;
    const base = process.env.REACT_APP_USER_API_URL;
    if (!base) return null;
    const res = await fetch(`${base}/handoff/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handoff }),
    });
    const data = await res.json().catch(() => ({}));
    const d = data && data.data;
    if (!res.ok || !d || !d.accessToken || !d.orgId) return null;
    const session = {
      orgId: d.orgId,
      accessKey: d.accessToken,
      refreshToken: d.refreshToken,
      role: d.role || null,
      userId: d.userId || null,
      username: d.user_name || null,
      email: d.email || null,
      deviceId: getOrCreateDeviceId(),
    };
    saveSessionToStorage(session);
    params.delete('h');
    const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '') + window.location.hash;
    window.history.replaceState({}, '', clean);
    return session;
  } catch (_) {
    return null;
  }
};
